/**
 * AgendaDashboardPanel - Compact agenda panel for Dashboard
 * Modern CRM style with date filter tabs and bookings list
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Car, User, RefreshCw, ExternalLink } from 'lucide-react';
import { useAgenda } from '../../../hooks/useAgenda';
import { cn } from '../../../lib/utils';

type DateFilterType = 'today' | 'week' | 'month' | 'year' | 'all';

const DATE_FILTERS: { type: DateFilterType; label: string }[] = [
  { type: 'today', label: 'Hoy' },
  { type: 'week', label: 'Semana' },
  { type: 'month', label: 'Mes' },
  { type: 'year', label: 'Año' },
  { type: 'all', label: 'Todas' },
];

function getDateRangeForFilter(type: DateFilterType): { startDate: Date; endDate: Date } | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (type) {
    case 'today':
      return { startDate: today, endDate: today };
    case 'week': {
      const startOfWeek = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startOfWeek.setDate(today.getDate() + diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return { startDate: startOfWeek, endDate: endOfWeek };
    }
    case 'month': {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate: startOfMonth, endDate: endOfMonth };
    }
    case 'year': {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      return { startDate: startOfYear, endDate: endOfYear };
    }
    case 'all':
    default:
      return null;
  }
}

function getSavedFilter(): DateFilterType {
  try {
    const saved = localStorage.getItem('dashboard-date-filter') as DateFilterType | null;
    if (saved && ['today', 'week', 'month', 'year', 'all'].includes(saved)) {
      return saved;
    }
  } catch {
    // Ignore
  }
  return 'today';
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  // Pre-arrival statuses
  UNVERIFIED: { label: 'Sin Verificar', color: 'bg-rose-50 text-rose-700' },
  PENDING_CONFIRMATION: { label: 'Pendiente', color: 'bg-amber-50 text-amber-700' },
  CONFIRMED: { label: 'Confirmada', color: 'bg-green-50 text-green-700' },
  AWAITING_DROPOFF: { label: 'Pendiente Entrega', color: 'bg-blue-50 text-blue-700' },
  DROPOFF_COMPLETE: { label: 'Entrega Completa', color: 'bg-cyan-50 text-cyan-700' },
  // Terminal statuses
  CANCELLED: { label: 'Cancelada', color: 'bg-red-50 text-red-700' },
  NO_SHOW: { label: 'No Show', color: 'bg-gray-200 text-gray-600' },
};

function formatTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface BookingItemProps {
  booking: {
    id: string;
    scheduledAt: number;
    bookingStatus: string;
    customer: { name: string };
    vehicle: { registration: string; make: string; model: string };
  };
}

function BookingItem({ booking }: BookingItemProps) {
  const statusConfig = STATUS_CONFIG[booking.bookingStatus] || STATUS_CONFIG.CONFIRMED;

  return (
    <Link
      to={`/bookings/${booking.id}`}
      className="flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-gray-50 transition-colors group"
    >
      <div className="flex-shrink-0 w-11 text-center">
        <span className="text-xs font-medium text-gray-900">
          {formatTime(booking.scheduledAt)}
        </span>
      </div>

      <div className="w-px h-8 bg-gray-200 group-hover:bg-blue-300 transition-colors" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="text-xs font-medium text-gray-900 truncate">
            {booking.customer.name}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Car className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="text-[11px] text-gray-500 truncate">
            {booking.vehicle.registration}
          </span>
        </div>
      </div>

      <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded', statusConfig.color)}>
        {statusConfig.label}
      </span>
    </Link>
  );
}

function BookingItemSkeleton() {
  return (
    <div className="flex items-center gap-2.5 px-2.5 py-2">
      <div className="w-11 h-4 bg-gray-100 rounded animate-pulse" />
      <div className="w-px h-8 bg-gray-100" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
        <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
      </div>
      <div className="h-4 w-14 bg-gray-100 rounded animate-pulse" />
    </div>
  );
}

export function AgendaDashboardPanel() {
  const [dateFilter, setDateFilter] = useState<DateFilterType>(getSavedFilter);

  const dateRange = useMemo(() => getDateRangeForFilter(dateFilter), [dateFilter]);

  const { bookings, summary, isLoading, refetch } = useAgenda({
    startDate: dateRange?.startDate || new Date(),
    endDate: dateRange?.endDate,
  });

  const sortedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => a.scheduledAt - b.scheduledAt);
  }, [bookings]);

  const handleFilterChange = (type: DateFilterType) => {
    setDateFilter(type);
    try {
      localStorage.setItem('dashboard-date-filter', type);
    } catch {
      // Ignore
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 flex flex-col" style={{ maxHeight: 'calc(100vh - 120px)' }}>
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-medium text-gray-900">Agenda</h2>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          title="Actualizar"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Date Filter Tabs */}
      <div className="p-2 border-b border-gray-100">
        <div className="flex gap-1">
          {DATE_FILTERS.map((filter) => (
            <button
              key={filter.type}
              onClick={() => handleFilterChange(filter.type)}
              className={cn(
                'flex-1 px-2 py-1.5 text-[10px] font-medium rounded transition-colors',
                dateFilter === filter.type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">
          {dateFilter === 'today' ? 'Hoy' :
           dateFilter === 'week' ? 'Esta semana' :
           dateFilter === 'month' ? 'Este mes' :
           dateFilter === 'year' ? new Date().getFullYear().toString() : 'Todas'}
        </span>
        {summary && (
          <span className="text-[11px] text-gray-500">
            {summary.total} cita{summary.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Bookings list */}
      <div className="flex-1 overflow-y-auto p-1.5 scrollbar-hide">
        {isLoading ? (
          <div className="space-y-0.5">
            <BookingItemSkeleton />
            <BookingItemSkeleton />
            <BookingItemSkeleton />
          </div>
        ) : sortedBookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Clock className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-xs text-gray-500">Sin citas para este dia</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {sortedBookings.map((booking) => (
              <BookingItem key={booking.id} booking={booking} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/50">
        <Link
          to="/jobs"
          className="flex items-center justify-center gap-1.5 w-full py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
        >
          Ver Tablero
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
